import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { fetchFeatureFlagAssignments } from 'apis/merchant-admin-server/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import useUser from 'common/hooks/useUser';
import { FeatureType } from 'common/types';

type UseCampaignFeatureFlagsProps = {
  feature?: FeatureType;
  featureId?: string;
  city?: string;
  deliveryType?: string;
  entityCountry?: Country | string;
  isPosEnabled?: boolean;
};

type CampaignFeatureFlags = Record<`is${CampaignType}EditEnabled`, boolean>;
type CampaignFeatureFlagsState = {
  campaignsFeatureFlags: CampaignFeatureFlags;
  isLoading: boolean;
};

const campaignTypesArray = Object.values(CampaignType);
const getFeatureFlagKey = (campaignType: CampaignType) => `is${campaignType}EditEnabled` as const;

const useCampaignFeatureFlags = ({
  feature,
  featureId,
  city,
  deliveryType,
  entityCountry,
  isPosEnabled,
}: UseCampaignFeatureFlagsProps): CampaignFeatureFlagsState => {
  const { isEmployee, user } = useUser();
  const intl = useIntl();
  const eligibilityCriteria = {
    is_employee: isEmployee.toString(),
    user_id: user?.id || '',
    user_email: user?.email || '',
    user_country: user?.country || '',
    user_locale: intl.locale,
    ...(deliveryType ? { delivery_type: deliveryType } : {}),
    ...(feature ? { feature } : {}),
    ...(featureId ? { feature_id: featureId } : {}),
    ...(city ? { city } : {}),
    ...(entityCountry ? { entity_country: entityCountry } : {}),
    ...(isPosEnabled !== undefined ? { is_pos_enabled: isPosEnabled } : {}),
  };

  const campaignsFeatureFlagsQuery = useQuery(
    ['/regatta/generic/feature_flag/assignments', ['merchant_campaigns_edit'], eligibilityCriteria],
    async () => {
      const flagResponses = await Promise.all(
        campaignTypesArray.map(async (campaignType: CampaignType) => {
          const flagResponse = await fetchFeatureFlagAssignments(['merchant_campaigns_edit'], {
            ...eligibilityCriteria,
            campaign_type: campaignType,
          });
          return [campaignType, flagResponse] as const;
        }),
      );

      return flagResponses.reduce(
        (acc, [campaignType, flagResponse]) => ({
          ...acc,
          [getFeatureFlagKey(campaignType)]: flagResponse.merchant_campaigns_edit === 'on',
        }),
        {} as CampaignFeatureFlags,
      );
    },
    {
      enabled: !!user,
      initialData: campaignTypesArray.reduce<CampaignFeatureFlags>(
        (acc, campaignType) => ({ ...acc, [getFeatureFlagKey(campaignType)]: false }),
        {} as CampaignFeatureFlags,
      ),
    },
  );

  return {
    campaignsFeatureFlags: campaignsFeatureFlagsQuery.data,
    isLoading: campaignsFeatureFlagsQuery.isLoading,
  };
};

export default useCampaignFeatureFlags;
